/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import get from 'lodash/get';
import update from 'immutability-helper';
import { tableActions, baseTableReducer } from 'carbon-addons-iot-react';

import { ROUTES } from '../../Common/constants/constants';

import {
  GET_PLATFORM_DEVICES,
  GET_PLATFORM_DEVICES_FAILURE,
  GET_PLATFORM_DEVICES_SUCCESS,
  GET_PLATFORM_DEVICE,
  GET_PLATFORM_DEVICE_FAILURE,
  GET_PLATFORM_DEVICE_SUCCESS,
  // TODO: UPDATE_DEVICE_TABLE_SORT,
  MQTT_STATUS_CONNECTING,
  // TODO: MQTT_STATUS_CONNECTED,
  MQTT_STATUS_SUBSCRIBING,
  MQTT_STATUS_SUBSCRIBE_SUCCESS,
  // TODO: MQTT_STATUS_SUBSCRIBE_FAILURE,
  MQTT_MESSAGE,
  MQTT_STATE_MESSAGE,
  UPDATE_DEVICE_SHOW_DELETE_CONFIRM,
  DELETE_PLATFORM_DEVICES_SUCCESS,
  DELETE_PLATFORM_DEVICES_FAILURE,
  DELETE_PLATFORM_DEVICE,
  DELETE_PLATFORM_DEVICE_SUCCESS,
  DELETE_PLATFORM_DEVICE_FAILURE,
  DEVICE_TABLE_SET_PAGE,
  DEVICE_TABLE_SELECT_ALL,
  DEVICE_TABLE_CLEAR_SEARCH,
  UPDATE_PLATFORM_DEVICE_SUCCESS,
  UPDATE_PLATFORM_DEVICE_FAILURE,
  GET_DEVICE_CONN_LOGS_SUCCESS,
  UPDATE_DEVICE_DETAIL_SHOW_DELETE_CONFIRM,
  DEVICE_TABLE_CLEAR_REDIRECT,
  UPDATE_SHOW_RECENT_EVENT_MODAL,
  UPDATE_SELECTED_EVENT,
} from 'devicesActionTypes';

export const devicesReducer = (
  state = {
    table: {
      data: [],
      fetchingData: false,
      fetchDataError: null,
      tableState: {
        instanceId: 'devicesTable',
        data: [],
        view: {
          filters: [],
          pagination: {
            pageSizes: [20, 50, 100],
            pageSize: 20,
            page: 1,
            totalItems: 0,
            maxPages: 10000000,
          },
          table: {
            isSelectAllSelected: false,
            sort: undefined,
            selectedIds: [],
            expandedIds: [],
            shouldApplyDynamicOrdering: true,
            loadingState: {
              isLoading: false,
              rowCount: 20, // page size
            },
            ordering: [
              {
                columnId: 'deviceId',
                isHidden: false,
              },
              {
                columnId: 'status',
                isHidden: false,
              },
              {
                columnId: 'typeId',
                isHidden: false,
              },
              {
                columnId: 'classId',
                isHidden: false,
              },
              {
                columnId: 'dateAdded',
                isHidden: false,
              },
              {
                columnId: 'description',
                isHidden: true,
              },
              {
                columnId: 'descriptiveLocation',
                isHidden: true,
              },
              {
                columnId: 'serialNumber',
                isHidden: true,
              },
              {
                columnId: 'deviceClass',
                isHidden: true,
              },
              {
                columnId: 'fwVersion',
                isHidden: true,
              },
              {
                columnId: 'hwVersion',
                isHidden: true,
              },
              {
                columnId: 'manufacturer',
                isHidden: true,
              },
              {
                columnId: 'model',
                isHidden: true,
              },
            ],
          },
          toolbar: {
            batchActions: [
              {
                id: 'delete-devices',
                labelText: 'Common.Delete',
                icon: 'delete',
                iconDescription: 'Common.Delete',
              },
            ],
            search: {
              defaultValue: undefined,
            },
          },
        },
      },
    },
    showDeleteConfirm: false,
    detail: {
      fetchingData: false,
      fetchDataError: null,
      recentEvents: {
        table: {
          view: {
            table: {
              loadingState: {
                isLoading: false,
                rowCount: 1,
              },
            },
          },
        },
        data: [],
      },
      state: {},
    },
    showDetailDeleteConfirm: false,
    showRecentEventModal: false,
    selectedEvent: {},
    initialTableSearchDefaultValue: undefined,
    redirectTo: undefined,
  },
  action
) => {
  switch (action.type) {
    case GET_PLATFORM_DEVICES_SUCCESS: {
      return update(state, {
        table: {
          fetchingData: { $set: false },
          fetchDataError: { $set: null },
          data: { $set: action.payload },
          tableState: {
            // we need to pass a set of IDs for the table to the baseTableReducer
            // so that the "select all" button can know which rows to select.  this
            // data-of-ids should track with the data rows rendered in the table itself.
            data: {
              $set: action.payload.results.map(i => ({
                id: `${i.typeId}:${i.deviceId}`,
              })),
            },
            view: {
              pagination: {
                totalItems: { $set: action.payload.meta.total_rows },
              },
            },
          },
        },
      });
    }
    case GET_PLATFORM_DEVICES:
      return {
        ...state,
        table: {
          ...state.table,
          fetchingData: action.type,
          fetchDataError: null,
        },
      };
    case GET_PLATFORM_DEVICES_FAILURE:
      return {
        ...state,
        table: {
          ...state.table,
          fetchingData: false,
          fetchDataError: action.payload,
        },
      };

    case GET_PLATFORM_DEVICE_SUCCESS:
      return update(state, {
        detail: {
          fetchingData: { $set: false },
          fetchDataError: { $set: null },
          $merge: {
            [`${action.payload.typeId}:${action.payload.deviceId}`]: action.payload,
          },
        },
      });

    case GET_DEVICE_CONN_LOGS_SUCCESS:
      return update(state, {
        detail: {
          fetchingData: { $set: false },
          fetchDataError: { $set: null },

          [`${action.typeId}:${action.deviceId}`]: {
            connectionLogs: { $set: action.payload },
          },
        },
      });

    case GET_PLATFORM_DEVICE:
      return update(state, {
        detail: {
          fetchingData: { $set: action.type },
          fetchDataError: { $set: null },
          recentEvents: { data: { $set: [] } },
        },
      });
    case GET_PLATFORM_DEVICE_FAILURE:
    case DELETE_PLATFORM_DEVICES_FAILURE:
    case DELETE_PLATFORM_DEVICE_FAILURE:
    case UPDATE_PLATFORM_DEVICE_FAILURE:
      return update(state, {
        detail: {
          fetchingData: { $set: false },
          fetchDataError: { $set: action.payload },
        },
      });

    case DELETE_PLATFORM_DEVICES_SUCCESS:
      return update(state, {
        detail: {
          fetchingData: { $set: false },
          fetchDataError: { $set: null },
        },
        table: {
          tableState: {
            view: {
              table: {
                isSelectAllSelected: { $set: false },
                selectedIds: { $set: [] },
                isSelectAllIndeterminate: { $set: false },
              },
            },
          },
        },
      });

    case DELETE_PLATFORM_DEVICE:
      return update(state, {
        detail: {
          fetchingData: { $set: action.type },
          fetchDataError: { $set: null },
        },
      });

    case DELETE_PLATFORM_DEVICE_SUCCESS:
      return update(state, {
        detail: {
          fetchingData: { $set: false },
          fetchDataError: { $set: null },
        },
        redirectTo: { $set: ROUTES.CONNECT },
      });

    case DEVICE_TABLE_SET_PAGE:
      return update(state, {
        table: {
          tableState: {
            view: {
              pagination: {
                page: { $set: action.page },
              },
            },
          },
        },
      });

    case DEVICE_TABLE_SELECT_ALL:
      return update(state, {
        table: {
          tableState: {
            view: {
              table: {
                isSelectAllSelected: {
                  $set: action.isSelected,
                },
                //
                selectedIds: {
                  $set: action.isSelected ? action.selectedIds : [],
                },
                isSelectAllIndeterminate: {
                  $set: false,
                },
              },
            },
          },
        },
      });

    case UPDATE_PLATFORM_DEVICE_SUCCESS:
      return update(state, {
        detail: {
          fetchingData: { $set: false },
          fetchDataError: { $set: null },
          [`${action.payload.typeId}:${action.payload.deviceId}`]: {
            $set: action.payload,
          },
        },
      });

    case UPDATE_DEVICE_DETAIL_SHOW_DELETE_CONFIRM:
      return update(state, {
        showDetailDeleteConfirm: { $set: action.show },
      });

    case MQTT_STATUS_CONNECTING:
    case MQTT_STATUS_SUBSCRIBING:
      return update(state, {
        detail: {
          recentEvents: {
            table: {
              view: {
                table: {
                  loadingState: {
                    isLoading: { $set: true },
                  },
                },
              },
            },
          },
        },
      });

    case MQTT_STATUS_SUBSCRIBE_SUCCESS:
      return update(state, {
        detail: {
          recentEvents: {
            table: {
              view: {
                table: {
                  loadingState: {
                    isLoading: { $set: false },
                  },
                },
              },
            },
          },
        },
      });

    case MQTT_MESSAGE:
      // TODO: cap length of recentEvents collection to some smallish number (10)
      // AND/OR add scrolling / pagination to table

      // construct an event object to go into state from the
      // details in the action (and the current system time)
      return update(state, {
        detail: {
          recentEvents: {
            data: {
              $push: [
                {
                  typeId: action.typeId,
                  deviceId: action.deviceId,
                  eventId: action.eventId,
                  format: action.format,
                  topic: action.type,
                  timestamp: Date.now(),
                  payload: action.payload,
                },
              ],
            },
          },
        },
      });
    case MQTT_STATE_MESSAGE: {
      let payloadAsString = '';
      // TODO: write as a .map or .reduce
      for (let i = 0; i < action.payload.length; i += 1) {
        payloadAsString += String.fromCharCode(parseInt(action.payload[i], 10));
      }
      return {
        ...state,
        detail: {
          ...state.detail,
          state: {
            ...state.detail.state,
            [action.interfaceId]: JSON.parse(payloadAsString),
          },
        },
      };
    }
    case UPDATE_DEVICE_SHOW_DELETE_CONFIRM:
      return update(state, {
        showDeleteConfirm: { $set: action.show },
      });

    case DEVICE_TABLE_CLEAR_SEARCH:
      return update(state, {
        table: {
          tableState: {
            view: {
              filters: { $set: [] },
              toolbar: {
                search: { $set: { defaultValue: undefined } },
              },
            },
          },
        },
        initialTableSearchDefaultValue: { $set: undefined },
      });

    case DEVICE_TABLE_CLEAR_REDIRECT:
      return update(state, {
        redirectTo: { $set: undefined },
      });

    case UPDATE_SHOW_RECENT_EVENT_MODAL:
      return update(state, {
        showRecentEventModal: { $set: action.show },
      });

    case UPDATE_SELECTED_EVENT:
      return update(state, {
        selectedEvent: { $set: action.event },
      });

    case tableActions.TABLE_SEARCH_APPLY:
      if (
        typeof action.instanceId === 'string' &&
        action.instanceId !== state.table.tableState.instanceId
      ) {
        return state;
      }
      return update(state, {
        initialTableSearchDefaultValue: { $set: action.payload },
      });

    case tableActions.TABLE_FILTER_CLEAR:
      if (
        typeof action.instanceId === 'string' &&
        action.instanceId !== state.table.tableState.instanceId
      ) {
        return state;
      }
      return update(state, {
        table: {
          tableState: {
            view: {
              filters: {
                $set: [],
              },
              pagination: {
                page: { $set: 1 },
              },
              toolbar: {
                search: {
                  defaultValue: { $set: '' },
                  value: { $set: undefined },
                },
              },
            },
          },
        },
      });

    default: // no-op - might be a table action
  }

  // To support instance matching (i.e. multiple table states in one store), just ensure
  // that a instanceId is provided on state passed to baseTableReducer.  If no instanceId
  // is provided, all matching actions will be processed by this table reducer.
  if (
    typeof action.instanceId === 'string' &&
    action.instanceId !== state.table.tableState.instanceId
  ) {
    return state;
  }

  switch (action.type) {
    case tableActions.TABLE_ACTION_CANCEL:
    case tableActions.TABLE_ACTION_APPLY:
    case tableActions.TABLE_ROW_CLICK:
    case tableActions.TABLE_COLUMN_SORT:
    case tableActions.TABLE_ROW_SELECT_ALL:
    case tableActions.TABLE_ROW_SELECT:
    case tableActions.TABLE_TOOLBAR_TOGGLE:
    case tableActions.TABLE_FILTER_APPLY:
    case tableActions.TABLE_EMPTY_STATE_ACTION:
    case tableActions.TABLE_COLUMN_ORDER:
    case tableActions.TABLE_ROW_EXPAND:
    case tableActions.TABLE_PAGE_CHANGE:
      return {
        ...state,
        table: {
          ...state.table,
          tableState: baseTableReducer(state.table.tableState, action),
        },
      };
    default:
      return state;
  }
};

// selectors
export const getDevices = state => get(state, 'devices.table.data.results') || [];
export const getDevicesBookmark = state => get(state, 'devices.table.data.bookmark') || null;
export const getDevicesTotalCount = state =>
  get(state, 'devices.table.data.meta.total_rows') || null;
export const isFetchingDevices = state => get(state, 'devices.table.fetchingData') !== false;
export const fetchDevicesError = state => get(state, 'devices.table.fetchDataError');
export const getDeviceTableView = state => get(state, 'devices.table.tableState.view');
export const getDeviceTableFilters = state => get(state, 'devices.table.tableState.view.filters');
export const getDeviceTableSearch = state =>
  get(state, 'devices.table.tableState.view.toolbar.search');

export const getDevice = (state, typeId, deviceId) =>
  get(state, ['devices', 'detail', `${typeId}:${deviceId}`]) || undefined;
export const isFetchingDevice = state =>
  get(state, 'devices.detail.fetchingData') === GET_PLATFORM_DEVICE;
export const fetchDeviceError = state => get(state, 'devices.detail.fetchDataError');

export const getRecentEventsData = state => get(state, 'devices.detail.recentEvents.data');
export const getRecentEventsTable = state => get(state, 'devices.detail.recentEvents.table');
export const getDeviceState = state => get(state, 'devices.detail.state');

export const getDeviceTableSort = state => get(state, 'devices.table.tableState.view.table.sort');
export const getDeviceShowDeleteConfirm = state => get(state, 'devices.showDeleteConfirm');
export const getDeviceDetailShowDeleteConfirm = state =>
  get(state, 'devices.showDetailDeleteConfirm');
export const getDeviceTableSelectedRowIds = state =>
  get(state, 'devices.table.tableState.view.table.selectedIds');
export const getDeviceTableInstanceId = state => get(state, 'devices.table.tableState.instanceId');
export const getDeviceTableData = state => get(state, 'devices.table.data');
export const getRedirectTo = state => get(state, 'devices.redirectTo');
export const getShowRecentEventModal = state => get(state, 'devices.showRecentEventModal');
export const getSelectedEvent = state => get(state, 'devices.selectedEvent');
export const getInitialTableSearchDefaultValue = state =>
  get(state, 'devices.initialTableSearchDefaultValue');
